import React from "react";
import { UserData } from "../redux/type";
import styled from "styled-components";
import { motion, easeOut } from "framer-motion";

import DangerousIcon from "@mui/icons-material/Dangerous";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";

interface GameOverPageProps {
  stats: UserData;
}

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #4dff5cff, #baf928ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #4dff56ff, #a9f928ff);
    border-radius: 2px;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
`;

const StatsContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  margin-bottom: 2rem;
`;

const StatItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
`;

const FinalMessage = styled(motion.p)`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  line-height: 1.6;
`;

const StyledSkullIcon = styled(DangerousIcon)`
  font-size: 4rem;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.2);
`;

const GameOverPage: React.FC<GameOverPageProps> = ({ stats }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: easeOut,
      },
    },
  };

  return (
    <Container initial="hidden" animate="visible" variants={containerVariants}>
      <Title variants={itemVariants}>Game Over</Title>
      <Subtitle variants={itemVariants}>
        Your journey has come to an end. Here's how you performed:
      </Subtitle>

      <StatsContainer variants={itemVariants}>
        <StatItem variants={itemVariants}>
          <StatLabel>
            <PersonIcon /> Username
          </StatLabel>
          <StatValue>{stats.username}</StatValue>
        </StatItem>

        <StatItem variants={itemVariants}>
          <StatLabel>
            <EmojiEventsIcon /> Total Points
          </StatLabel>
          <StatValue>{stats.points}</StatValue>
        </StatItem>

        <StatItem variants={itemVariants}>
          <StatLabel>
            <WhatshotIcon /> Daily Streak
          </StatLabel>
          <StatValue>
            {stats.lifetimeLoginDays + stats.dailyStreak} days
          </StatValue>
        </StatItem>

        <StatItem variants={itemVariants}>
          <StatLabel>
            <CalendarTodayIcon /> Completed Messions
          </StatLabel>
          <StatValue>{stats.completedTasks.length}</StatValue>
        </StatItem>
      </StatsContainer>

      <FinalMessage variants={itemVariants}>
        Thank you for playing. Your account has been permanently retired.
        <br />
        We hope you enjoyed your time with us!
      </FinalMessage>
    </Container>
  );
};

export default GameOverPage;
